import { c, classy, m, } from '@onfido/castor';
import { Input, OptionList, Popover, } from '@onfido/castor-react';
import React, { useCallback, useMemo, useRef, useState, } from 'react';
import { MaybeIcon } from '../../internal';
import { textContent, withRef } from '../../utils';
import { OptionListInit } from '../option-list/options-list-init';
/**
 * `Combobox` by default uses two `Icon` that require `Icons` (SVG sprite) to be
 * included in your app.
 *
 * https://github.com/onfido/castor-icons#use-with-plain-code
 *
 * You may also provide any other SVG element via the `icon` and `selectedIcon`
 * props, but using Castor iconography is recommended.
 */
export const Combobox = withRef(function Combobox({ id: initialId, align = 'start', children, className, defaultValue, disabled, empty = 'No matching options', icon, name, onBlur, onChange, onClick, onKeyUp, position = 'bottom', required, selectedIcon, value, ...restProps }, ref) {
    var _a, _b, _c;
    const inputRef = useRef(null);
    const selectRef = useRef(null);
    const optionsRef = useRef(null);
    const [input, setInput] = useState('');
    const [search, setSearch] = useState();
    const [placeholder, setPlaceholder] = useState();
    const [selected, setSelected] = useState({});
    const [open, setOpen] = useState(false);
    const preventBlur = useRef(false);
    const id = useMemo(() => `castor_combobox_${++idCount}`, [initialId]);
    const propagateOnChange = useCallback(() => {
        // propagate `onChange` manually because <select> won't naturally when its
        // value is changed programatically by React, and on next tick, because
        // React needs to update its value first
        setTimeout(() => { var _a; return (_a = selectRef.current) === null || _a === void 0 ? void 0 : _a.dispatchEvent(new Event('change', { bubbles: true })); });
    }, []);
    const close = () => setOpen(false);
    return (React.createElement("div", { ref: ref, className: classy(c('combobox'), m({ open }), className) },
        React.createElement("select", { ref: selectRef, name: name, disabled: disabled, required: required, hidden: true, onChange: (_a = onChange) !== null && _a !== void 0 ? _a : (() => void 0) }, !selected.value || React.createElement("option", { hidden: true, value: selected.value })),
        React.createElement(Input, { ...restProps, ref: inputRef, id: id, role: "combobox", value: input, placeholder: placeholder, autoComplete: "off", disabled: disabled, required: required, onBlur: (event) => {
                if (preventBlur.current)
                    return (preventBlur.current = false);
                setInput(textContent(selected.option));
                setSearch(undefined);
                close();
                onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
            }, onChange: (event) => {
                setInput(event.target.value);
                setSearch(event.target.value);
            }, onClick: (event) => {
                if (!open) {
                    setOpen(true);
                    preventBlur.current = true;
                }
                onClick === null || onClick === void 0 ? void 0 : onClick(event);
            }, onKeyUp: (event) => {
                var _a;
                // ignore 'Tab' - expect arrow up/down keys to open when/if pressed
                if (event.key !== 'Tab') {
                    // select first option if confirming when focus is still on inputRef
                    if (open && confirmKeys.has(event.key) && !selected.value) {
                        select((_a = optionsRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('input:enabled'));
                    }
                    // arrow up and down keys move inside popover
                    else if (navigateKeys.has(event.key)) {
                        if (!open)
                            setOpen(true);
                        preventBlur.current = true;
                        setTimeout(() => { var _a; return focus((_a = optionsRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('input:enabled')); });
                    }
                    // close if 'Esc' is pressed, otherwise open on any other key
                    else {
                        setOpen(!closeKeys.has(event.key));
                    }
                }
                onKeyUp === null || onKeyUp === void 0 ? void 0 : onKeyUp(event);
            }, "aria-controls": `${id}_options`, "aria-autocomplete": "list", "aria-expanded": open }),
        React.createElement(MaybeIcon, { icon: icon, name: "chevron-down" }),
        open && (React.createElement(Popover, { align: align, className: classy(c('combobox-dropdown')), overlay: true, position: position, target: inputRef, onClose: () => {
                setInput(textContent(selected.option));
                setSearch(undefined);
                close();
            }, 
            // stop bubbling so that Field validation isn't affected
            onBlur: stopPropagation, onChange: stopPropagation, onInvalid: stopPropagation },
            React.createElement(OptionList, { ref: optionsRef, id: `${id}_options`, defaultValue: defaultValue, icon: selectedIcon, name: name, search: search, value: (_c = (_b = selected.value) !== null && _b !== void 0 ? _b : value) !== null && _c !== void 0 ? _c : defaultValue, onChange: (selected) => {
                    preventBlur.current = false;
                    setSelected(selected);
                    setInput(textContent(selected.option));
                    setSearch(undefined);
                    propagateOnChange();
                    focus(inputRef.current);
                    close();
                }, onKeyDown: (event) => {
                    // ignore confirmation keys
                    if (confirmKeys.has(event.key))
                        return;
                    // reset state on closing key press
                    else if (closeKeys.has(event.key)) {
                        focus(inputRef.current);
                        close();
                    }
                    // close if focus moves outside
                    else if (event.key === 'Tab') {
                        close();
                        event.preventDefault();
                    }
                    // any other key is assumed to be typing on <input>
                    else if (!navigateKeys.has(event.key)) {
                        if (moveCursorKeys.has(event.key))
                            event.preventDefault();
                        focus(inputRef.current);
                    }
                }, onKeyUp: (event) => {
                    // close on confirmation key press after change happened
                    if (confirmKeys.has(event.key))
                        return close();
                }, onPointerDown: () => {
                    preventBlur.current = true;
                } },
                children,
                React.createElement("div", { className: classy(c('option-content'), m('empty')) }, empty)))),
        !placeholder && (React.createElement(OptionListInit, { defaultValue: defaultValue, value: value, onInit: (selected) => {
                setSelected(selected);
                if (!selected.value)
                    setPlaceholder(textContent(selected.option));
                else {
                    setInput(textContent(selected.option));
                    propagateOnChange();
                }
            } }, children))));
});
const closeKeys = new Set(['Escape', 'Enter']);
const confirmKeys = new Set(['Enter']);
const moveCursorKeys = new Set(['ArrowLeft', 'ArrowRight']);
const navigateKeys = new Set(['ArrowDown', 'ArrowUp', 'Enter']);
const focus = (element) => element === null || element === void 0 ? void 0 : element.focus({ preventScroll: true });
// dispatch event to fake pressing 'Enter' to select option and close popover
const select = (element) => element === null || element === void 0 ? void 0 : element.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true, key: 'Enter' }));
const stopPropagation = (event) => event.stopPropagation();
let idCount = 0;
//# sourceMappingURL=combobox.js.map